import { useEffect, useState } from "react";
import { Wallet } from "lucide-react";
import { dictionaries } from "../lib/i18n";
import type { Budget, CurrencyCode, UiLang } from "../lib/types";

interface Props {
  budget: Budget;
  lang: UiLang;
}

/**
 * Hva turen koster.
 *
 * Vist som et spenn, aldri som ett tall. Forskjellen mellom en DNT-hytte og et
 * hotell i Bergen er ikke støy — det er selve valget den reisende står i, og
 * et gjennomsnitt ville skjult det.
 *
 * Forbeholdet står under, ikke gjemt bak et spørsmålstegn: dette er satser,
 * ikke priser hentet fra noen som selger. Ingen av aktørene som selger
 * togbilletter, ferjer eller senger i Norge har et åpent pris-API.
 */

/** Hvor valgt valuta huskes. En turist bytter den én gang, ikke hver gang. */
const STORAGE_KEY = "reise-planer:currency";

function rememberedCurrency(): CurrencyCode {
  try {
    return (localStorage.getItem(STORAGE_KEY) as CurrencyCode) || "NOK";
  } catch {
    // Privat vindu eller blokkert lagring. Kroner er et greit utgangspunkt.
    return "NOK";
  }
}

/**
 * Beløpet i valgt valuta.
 *
 * Rundes til to signifikante trinn — hundre kroner, ti euro — fordi et anslag
 * med et spenn på 20 000 kroner ikke skal presentere entall.
 */
function money(nok: number, currency: CurrencyCode, rates: Budget["rates"], lang: UiLang): string {
  const perUnit = currency === "NOK" ? 1 : (rates[currency] ?? 0);
  // Uten kurs vises kroner heller enn et tall som utgir seg for noe annet.
  const effective = perUnit > 0 ? currency : "NOK";
  const value = perUnit > 0 ? nok / perUnit : nok;

  const step = value >= 2000 ? 100 : value >= 200 ? 10 : 1;
  const rounded = Math.round(value / step) * step;

  return new Intl.NumberFormat(lang === "no" ? "nb-NO" : "en-GB", {
    style: "currency",
    currency: effective,
    maximumFractionDigits: 0,
  }).format(rounded);
}

export function BudgetPanel({ budget, lang }: Props) {
  const t = dictionaries[lang];
  const [currency, setCurrency] = useState<CurrencyCode>(rememberedCurrency);

  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, currency);
    } catch {
      // Lagring blokkert — valget varer da bare denne økten.
    }
  }, [currency]);

  // Bare valutaene vi faktisk har kurs for. Kroner står først og alltid, og
  // Set-en luker den bort fra kurslista, der den også ligger med verdien 1.
  const available = [...new Set<CurrencyCode>(["NOK", ...(Object.keys(budget.rates) as CurrencyCode[])])];
  const show = (nok: number) => money(nok, currency, budget.rates, lang);

  return (
    <div className="card budget">
      <div className="card-title">
        <Wallet size={14} style={{ verticalAlign: -2, marginRight: 7 }} />
        {t.budgetTitle}
      </div>
      <div className="budget-head">
        <p className="card-sub">{t.budgetPerPerson(budget.travellers)}</p>
        <label className="budget-currency">
          <span className="sr-only">{t.budgetCurrency}</span>
          <select
            value={currency}
            onChange={(event) => setCurrency(event.target.value as CurrencyCode)}
            aria-label={t.budgetCurrency}
          >
            {available.map((code) => (
              <option key={code} value={code}>
                {code}
              </option>
            ))}
          </select>
        </label>
      </div>

      <ul className="budget-lines">
        {budget.lines.map((line) => {
          // Bredden viser hvor stor andel linja utgjør av det høyeste anslaget.
          const share = budget.high > 0 ? (line.high / budget.high) * 100 : 0;
          return (
            <li className="budget-line" key={line.key}>
              <span className="budget-label">{line.label}</span>
              <span className="budget-bar" aria-hidden="true">
                <span className={`budget-fill budget-fill--${line.key}`} style={{ width: `${share}%` }} />
              </span>
              <span className="budget-amount">
                {t.budgetRange(show(line.low), show(line.high))}
              </span>
            </li>
          );
        })}
      </ul>

      <div className="budget-total">
        <span>{t.budgetTotal}</span>
        <strong>{t.budgetRange(show(budget.low), show(budget.high))}</strong>
      </div>

      {/*
        Kostnadsdelingen.

        Vises bare der noe faktisk deles. På tog og buss er hver linje en
        billett, `shared` er null, og de fire kolonnene ville vist samme tall
        fire ganger under en overskrift som lover en besparelse — altså et løfte
        om noe appen selv har regnet ut at ikke finnes.

        Tallet er ærlig om størrelsen sin. Å dele drivstoff, bom og ferje på
        fire sparer noen hundre kroner per hode på en norsk uke, ikke halve
        turen: det er senga og maten som koster, og de står per person. Derfor
        står `splitNote` under kolonnene og ikke i en tooltip — den er
        forskjellen mellom et anslag man kan stole på og en salgsplakat.
      */}
      {budget.shared && budget.shared.high > 0 && budget.perPerson && budget.perPerson.length > 0 && (
        <div className="budget-split">
          <h4 className="budget-split-title">{t.splitTitle}</h4>
          <p className="budget-split-lead">
            {t.splitLead(t.budgetRange(show(budget.shared.low), show(budget.shared.high)))}
          </p>

          <ul className="budget-split-seats">
            {budget.perPerson.map((split) => {
              const yours = split.travellers === budget.travellers;
              return (
                <li
                  className={yours ? "budget-seat budget-seat--yours" : "budget-seat"}
                  key={split.travellers}
                  /*
                   * `aria-current` framfor bare en farge: hvilken kolonne som
                   * er brukerens egen gruppe er hele grunnen til at radet er
                   * lesbart, og en skjermleser ser ikke rammen.
                   */
                  aria-current={yours ? "true" : undefined}
                >
                  <span className="budget-seat-count">
                    {split.travellers === 1 ? t.splitAlone : t.splitSeats(split.travellers)}
                  </span>
                  <strong className="budget-seat-amount">
                    {t.budgetRange(show(split.low), show(split.high))}
                  </strong>
                  {yours && <em className="budget-seat-yours">{t.splitYours}</em>}
                </li>
              );
            })}
          </ul>

          <p className="budget-split-note">{t.splitNote}</p>
        </div>
      )}

      {/*
        Drivstoff og strøm i mengde.

        Kronene står over; dette er det som holder seg. Bensinprisen endrer seg
        hvert kvartal, men arbeidet ruta krever gjør ikke det — og stigningen er
        hele forskjellen mellom dette og kilometer delt på ti.
      */}
      {budget.energy && (
        <p className="budget-energy">
          {t.energySummary(budget.energy.litres, budget.energy.kWh, budget.energy.ascentM)}
        </p>
      )}

      {/*
        Bomanleggene med navn.

        Dette er den ene linja i budsjettet som ikke er en sats: satsene kommer
        fra NVDB og er de faktiske. Da skal de også kunne etterprøves, og derfor
        står navnet på anlegget og lenken til den som fakturerer.

        Skillet mellom sikkert og mulig er ikke pynt. Bomringen i byen ruta
        starter i betaler man uansett hvilken vei man kjører; et veiprosjekt
        over Hardangervidda gjelder bare hvis man tar nettopp den veien, og
        appen har ingen veidata å avgjøre det med.
      */}
      {budget.tolls && (budget.tolls.certain.length > 0 || budget.tolls.possible.length > 0) && (
        <div className="budget-tolls">
          {budget.tolls.certain.length > 0 && (
            <>
              <h4 className="budget-tolls-title">{t.tollCertainTitle}</h4>
              <ul className="budget-tolls-list">
                {budget.tolls.certain.map((scheme) => (
                  <li key={scheme.name}>
                    {scheme.website ? (
                      <a href={scheme.website} target="_blank" rel="noreferrer">
                        {scheme.name}
                      </a>
                    ) : (
                      <span>{scheme.name}</span>
                    )}
                    <span className="budget-tolls-rate">{show(scheme.smallCar ?? 0)}</span>
                  </li>
                ))}
              </ul>
            </>
          )}

          {budget.tolls.possible.length > 0 && (
            <>
              <h4 className="budget-tolls-title">{t.tollPossibleTitle}</h4>
              <ul className="budget-tolls-list">
                {budget.tolls.possible.map((scheme) => (
                  <li key={scheme.name}>
                    {scheme.website ? (
                      <a href={scheme.website} target="_blank" rel="noreferrer">
                        {scheme.name}
                      </a>
                    ) : (
                      <span>{scheme.name}</span>
                    )}
                    <span className="budget-tolls-rate">{show(scheme.smallCar ?? 0)}</span>
                  </li>
                ))}
              </ul>
            </>
          )}

          <p className="budget-tolls-note">
            {t.tollDiscountNote(Math.round(budget.tolls.discount * 100))}
          </p>
        </div>
      )}

      <p className="budget-note">
        {budget.disclaimer}
        {/* Kursdatoen står der, så tallet kan etterprøves. */}
        {currency !== "NOK" && budget.rateDate && ` ${t.budgetRateNote(budget.rateDate)}`}
      </p>
    </div>
  );
}
